import './EditableDropdown.scss';
import cn from 'classnames';
import { ComponentProps, ReactElement, useRef, useState } from 'react';
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { MdAdd, MdContentCopy, MdDelete, MdEdit, MdMoreVert } from 'react-icons/md';
import { dateToMddyy } from '../../utils/date-utils';
import { Button } from '../Button';
import { Appearance, Placement, PlacementType, Size } from '../designConstants';
import { Dropdown } from '../Dropdown/Dropdown';
import { DropdownBaseProps } from '../Dropdown/dropdown-constants';
import { InteractiveDiv } from '../InteractiveEl';
import { transformEmailToFullName } from '../../utils/text-utils';
import RenameIcon from '../RenameModal/RenameIcon.svg?react';

export interface EditableDropdownItem {
  id: number;
  label: string;
  updatedBy?: string;
  updatedAt?: string;
  isDeletable?: boolean;
}

// Presence of the button callbacks (or lack thereof) determines each action's existence.
interface EditableDropdownProps extends Omit<
  DropdownBaseProps,
  'options' | 'className' | 'label' | 'clearable' | 'onClear' | 'menuPortalTarget'
> {
  addItemOnClick?: () => void;
  addItemText?: string;
  applyOnClick?: () => void;
  cancelOnClick?: () => void;
  className?: string;
  deleteItemOnClick?: (item: EditableDropdownItem) => void;
  // edit/duplicate receive the item id (delete/rename receive the whole item) —
  // this asymmetry is inherited verbatim from the original bpm-frontend component.
  duplicateItemOnClick?: (id: EditableDropdownItem['id']) => void;
  renameItemOnClick?: (item: EditableDropdownItem) => void;
  editItemOnClick?: (id: EditableDropdownItem['id']) => void;
  actionMenuSubtitles?: string[];
  hasTicks?: boolean;
  dropdownLabel?: string;
  maxLabelLength?: number;
  menuItems: EditableDropdownItem[];
  selectedItem: EditableDropdownItem | undefined;
  setSelectedItem: (item: EditableDropdownItem | undefined) => void;
  disabledDeleteTooltipText?: string;
  overlayPlacement?: PlacementType;
}

export const EditableDropdown = ({
  size = Size.LG,
  overlayPlacement = Placement.RIGHT,
  addItemOnClick,
  addItemText = '',
  applyOnClick,
  cancelOnClick,
  className = '',
  deleteItemOnClick,
  duplicateItemOnClick,
  renameItemOnClick,
  editItemOnClick,
  actionMenuSubtitles = [],
  hasTicks = false,
  dropdownLabel = '',
  maxLabelLength,
  menuItems,
  selectedItem,
  setSelectedItem,
  disabledDeleteTooltipText,
  ...dropdownPassthroughProps
}: EditableDropdownProps) => {
  const [selectedOptionTrigger, setSelectedOptionTrigger] = useState<number | undefined>(undefined);
  const [showDropdown, setShowDropdown] = useState(false);
  const disabledActionClickedRef = useRef(false);

  const fullLabel = selectedItem?.label ?? dropdownLabel;
  const shouldTruncate =
    selectedItem != null && maxLabelLength != null && fullLabel.length > maxLabelLength;

  const labelElement = (
    <div
      className={cn('editable-dropdown-label', {
        'placeholder-label': !selectedItem && !showDropdown
      })}
      style={maxLabelLength != null ? { maxWidth: `${maxLabelLength}ch` } : undefined}
    >
      {fullLabel}
    </div>
  );

  const hasItemActions = Boolean(
    editItemOnClick || renameItemOnClick || deleteItemOnClick || duplicateItemOnClick
  );

  const processedMenuItems = menuItems.map(item => {
    const createdBy = item.updatedBy ? transformEmailToFullName(item.updatedBy) : 'N/A';
    const createdOn = item.updatedAt ? dateToMddyy(item.updatedAt) : undefined;
    const isDeletable = item.isDeletable ?? true;

    return {
      className: selectedOptionTrigger === item.id ? 'dropdown-open' : '',
      label: item.label,
      value: `${item.id}`,
      selected: Boolean(selectedItem && selectedItem.id === item.id),
      itemOptions: hasItemActions ? (
        <ItemActions
          item={item}
          isOpen={selectedOptionTrigger === item.id}
          isDeletable={isDeletable}
          createdBy={createdBy}
          createdOn={createdOn}
          disabledDeleteTooltipText={disabledDeleteTooltipText}
          editItemOnClick={editItemOnClick}
          renameItemOnClick={renameItemOnClick}
          deleteItemOnClick={deleteItemOnClick}
          duplicateItemOnClick={duplicateItemOnClick}
          onToggleOpen={() =>
            setSelectedOptionTrigger(current => (current === item.id ? undefined : item.id))
          }
          onDeleteClick={() => {
            deleteItemOnClick?.(item);
            setSelectedOptionTrigger(undefined);
          }}
          onDisabledDeleteClick={() => {
            disabledActionClickedRef.current = true;
          }}
        />
      ) : undefined
    };
  });

  const addItemButton = (
    <Button
      size={Size.LG}
      className='add-new-item-button'
      appearance={Appearance.EMPTY}
      leadingElement={<MdAdd />}
    >
      {addItemText}
    </Button>
  );

  return (
    <div className={cn('editable-dropdown', className, { selected: Boolean(selectedItem) })}>
      <Dropdown
        {...dropdownPassthroughProps}
        size={size}
        overlayPlacement={overlayPlacement}
        applyOnClick={applyOnClick}
        hideApplyOnClick={applyOnClick === undefined}
        cancelOnClick={cancelOnClick}
        hideCancelOnClick={cancelOnClick === undefined}
        footer={addItemOnClick ? addItemButton : undefined}
        footerOnClick={addItemOnClick}
        hasFilterBar={false}
        hasTicks={hasTicks}
        label={
          <LabelWithTooltip show={shouldTruncate} fullLabel={fullLabel}>
            {labelElement}
          </LabelWithTooltip>
        }
        menuItems={processedMenuItems}
        multiSelect={false}
        onChange={value => {
          setSelectedItem(menuItems.find(item => item.id === Number.parseInt(value, 10)));
          setSelectedOptionTrigger(undefined);
          setShowDropdown(false);
        }}
        show={showDropdown}
        setShow={setShowDropdown}
        subTitles={actionMenuSubtitles}
        onToggle={isOpen => {
          if (selectedOptionTrigger != null) {
            if (disabledActionClickedRef.current) {
              disabledActionClickedRef.current = false;
            } else {
              setShowDropdown(true);
              setSelectedOptionTrigger(undefined);
            }
          } else {
            setShowDropdown(isOpen);
          }
        }}
        options={[]}
        value=''
      />
    </div>
  );
};

interface LabelWithTooltipProps {
  show: boolean;
  fullLabel: string;
  children: ReactElement;
}

const LabelWithTooltip = ({ show, fullLabel, children }: LabelWithTooltipProps) => {
  if (!show) return children;

  return (
    <OverlayTrigger
      placement={Placement.BOTTOM}
      overlay={<Tooltip id='editable-dropdown-label-tooltip'>{fullLabel}</Tooltip>}
    >
      {children}
    </OverlayTrigger>
  );
};

interface ItemActionsProps {
  item: EditableDropdownItem;
  isOpen: boolean;
  isDeletable: boolean;
  createdBy: string;
  createdOn?: string;
  disabledDeleteTooltipText?: string;
  editItemOnClick?: (id: EditableDropdownItem['id']) => void;
  renameItemOnClick?: (item: EditableDropdownItem) => void;
  deleteItemOnClick?: (item: EditableDropdownItem) => void;
  duplicateItemOnClick?: (id: EditableDropdownItem['id']) => void;
  onToggleOpen: () => void;
  onDeleteClick: () => void;
  onDisabledDeleteClick: () => void;
}

const ItemActions = ({
  item,
  isOpen,
  isDeletable,
  createdBy,
  createdOn,
  disabledDeleteTooltipText,
  editItemOnClick,
  renameItemOnClick,
  deleteItemOnClick,
  duplicateItemOnClick,
  onToggleOpen,
  onDeleteClick,
  onDisabledDeleteClick
}: ItemActionsProps) => (
  <OverlayTrigger
    show={isOpen}
    placement={Placement.RIGHT_START}
    overlay={
      <Popover id={`editable-dropdown-options-${item.id}`}>
        <div className='editable-dropdown-options'>
          {editItemOnClick && (
            <OptionRow icon={<MdEdit />} label='Edit' onClick={() => editItemOnClick(item.id)} />
          )}
          {renameItemOnClick && (
            <OptionRow
              icon={<RenameIcon />}
              label='Rename'
              onClick={() => renameItemOnClick(item)}
            />
          )}
          {deleteItemOnClick && (
            <DeleteOption
              isDeletable={isDeletable}
              disabledDeleteTooltipText={disabledDeleteTooltipText}
              onDelete={onDeleteClick}
              onDisabledClick={onDisabledDeleteClick}
            />
          )}
          {duplicateItemOnClick && (
            <OptionRow
              icon={<MdContentCopy />}
              label='Duplicate'
              onClick={() => duplicateItemOnClick(item.id)}
            />
          )}
          <div className='editable-dropdown-last-modified'>
            <div className='editable-dropdown-last-modified-text'>Created By: {createdBy}</div>
            {createdOn && (
              <div className='editable-dropdown-last-modified-text'>Created On: {createdOn}</div>
            )}
          </div>
        </div>
      </Popover>
    }
  >
    <InteractiveDiv className='editable-dropdown-option-expander' onClick={onToggleOpen}>
      <MdMoreVert />
    </InteractiveDiv>
  </OverlayTrigger>
);

// Extends InteractiveDiv's props so react-bootstrap's OverlayTrigger can clone a ref
// and its hover/focus handlers onto the row when it is used as a tooltip trigger.
interface OptionRowProps extends ComponentProps<typeof InteractiveDiv> {
  icon: ReactElement;
  label: string;
}

const OptionRow = ({ icon, label, className, ...interactiveProps }: OptionRowProps) => (
  <InteractiveDiv {...interactiveProps} className={cn('editable-dropdown-option', className)}>
    <div className='editable-dropdown-option-icon'>{icon}</div>
    <div className='editable-dropdown-option-text'>{label}</div>
  </InteractiveDiv>
);

interface DeleteOptionProps {
  isDeletable: boolean;
  disabledDeleteTooltipText?: string;
  onDelete: () => void;
  onDisabledClick: () => void;
}

const DeleteOption = ({
  isDeletable,
  disabledDeleteTooltipText,
  onDelete,
  onDisabledClick
}: DeleteOptionProps) => {
  const row = (
    <OptionRow
      icon={<MdDelete />}
      label='Delete'
      className={cn({ 'disabled-delete': !isDeletable })}
      onClick={isDeletable ? onDelete : onDisabledClick}
    />
  );

  if (!isDeletable && disabledDeleteTooltipText) {
    return (
      <OverlayTrigger
        placement={Placement.TOP}
        overlay={
          <Tooltip id='editable-dropdown-delete-tooltip'>{disabledDeleteTooltipText}</Tooltip>
        }
      >
        {row}
      </OverlayTrigger>
    );
  }

  return row;
};
